import React, { useEffect, useState } from 'react';
import { Form, Card, Button, Dropdown, Input, Icon, Message, Checkbox } from 'semantic-ui-react';
import { I18n, Translate } from 'react-redux-i18n';
import { useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { useAppDispatch } from '../../../hooks';
import { RootState } from '../../../store';
import { TemplateAutocomplete } from '../../../components/template/templateAutocomplete';
import ValidatedInput from '../../../components/common/validatedInput';
import { createAccessKey, clearAccessKeyErrors } from '../store/accessKeyActions';
import RecordResultMessage from '../../../components/common/recordResult';
import SavedEntityModal from '../../../components/common/savedEntityModal';
import Text from '../../../components/common/text';
import { maskClientKey } from '../../../models/AccessKey';
import { systemSettingsRoutes } from '../../systemSettings/constants';
import { registrationPlatformOptions } from '../../../helpers/externalIntegrationSystem';
import {
  getEntityOption,
  getOrganizationOption
} from '../../../helpers/entityOptionHelper';
import { markSystemOnlyOptions } from '../../../components/common/SystemOnlyBadge';

// Key type constants
const KEY_TYPES = {
  ZAPIER: 'Zapier',
  MOVE_DATA: 'MoveData',
  OTHER: 'Other',
  REGISTRATION_PLATFORM: 'RegistrationPlatform'
} as const;

// Create flags preset for a registration platform key: Donation, Fundraiser, Team
const REGISTRATION_PLATFORM_CREATE_FLAGS = [8, 128, 16384];

type KeyType = typeof KEY_TYPES[keyof typeof KEY_TYPES];

const operationEntityFlags = [
  { text: 'Campaign', value: 4 },
  { text: 'Donation', value: 8 },
  { text: 'Event', value: 64 },
  { text: 'Fundraiser', value: 128 },
  { text: 'Microblog', value: 256 },
  { text: 'Organization', value: 512 },
  { text: 'Page', value: 1024 },
  { text: 'Sale', value: 2048 },
  { text: 'SaleDetail', value: 4096 },
  { text: 'Team', value: 16384 },
  { text: 'Ticket', value: 65536 },
  { text: 'Collection', value: 262144 },
  { text: 'DonationSubscription', value: 524288 }
];

const AccessKeyInput = () => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const accessKeyState = useSelector((s: RootState) => (s as any).accessKey);
  const session = useSelector((s: RootState) => s.session);
  const [scope, setScope] = useState<'org' | 'campaignOrganizer'>('org');
  const [orgEntity, setOrgEntity] = useState<any>(null);
  const [campaignOrganizerEntity, setCampaignOrganizerEntity] = useState<any>(null);
  const [description, setDescription] = useState('');
  const [keyType, setKeyType] = useState<KeyType | ''>('');
  const [platform, setPlatform] = useState('');
  const [readOnly, setReadOnly] = useState(true);
  const [noSecret, setNoSecret] = useState(false);
  const [advancedPermissions, setAdvancedPermissions] = useState(false);
  const [readAllowed, setReadAllowed] = useState<number[]>([]);
  const [createAllowed, setCreateAllowed] = useState<number[]>([]);
  const [editAllowed, setEditAllowed] = useState<number[]>([]);
  const [deleteAllowed, setDeleteAllowed] = useState<number[]>([]);
  const [showModal, setShowModal] = useState(false);
  const [createdKey, setCreatedKey] = useState<{ clientKey: string; clientSecret: string } | null>(null);
  const [modalLocked, setModalLocked] = useState(false); // prevents re-evaluation closing modal
  const [copied, setCopied] = useState<{ key?: boolean; secret?: boolean }>({});

  const isPlatformKey = keyType === KEY_TYPES.REGISTRATION_PLATFORM;

  const keyTypeOptions = [
    { text: I18n.t('accessKeys.page.input.keyType-zapier') || KEY_TYPES.ZAPIER, value: KEY_TYPES.ZAPIER },
    { text: I18n.t('accessKeys.page.input.keyType-movedata') || KEY_TYPES.MOVE_DATA, value: KEY_TYPES.MOVE_DATA },
    { text: I18n.t('accessKeys.page.input.keyType-other') || KEY_TYPES.OTHER, value: KEY_TYPES.OTHER },
    ...(session.isSystemAdmin
      ? [
          {
            text: I18n.t('accessKeys.page.input.keyType-registrationPlatform'),
            value: KEY_TYPES.REGISTRATION_PLATFORM,
            systemOnly: true
          }
        ]
      : [])
  ];

  useEffect(() => {
    if (keyType === KEY_TYPES.ZAPIER) {
      setDescription(KEY_TYPES.ZAPIER);
      setReadOnly(false);
      setNoSecret(false);
      setAdvancedPermissions(false);
    } else if (keyType === KEY_TYPES.MOVE_DATA) {
      setDescription(KEY_TYPES.MOVE_DATA);
      setReadOnly(true);
      setNoSecret(false);
      setAdvancedPermissions(false);
    } else if (keyType === KEY_TYPES.REGISTRATION_PLATFORM) {
      setReadOnly(false);
      setNoSecret(false);
      setAdvancedPermissions(true);
      setReadAllowed([]);
      setCreateAllowed(REGISTRATION_PLATFORM_CREATE_FLAGS);
      setEditAllowed([]);
      setDeleteAllowed([]);
    }
  }, [keyType]);

  useEffect(() => {
    if (isPlatformKey && platform) {
      const label =
        registrationPlatformOptions().find((item) => item.value === platform)
          ?.text || '';
      setDescription(label.substring(0, 20));
    }
  }, [platform, isPlatformKey]);

  const buildFlagsValue = (vals: number[]) => (vals || []).reduce((a, b) => a | b, 0);

  const isAdvancedInvalid = advancedPermissions && [readAllowed, createAllowed, editAllowed, deleteAllowed].every(a => a.length === 0);

  const selectedEntity = scope === 'org' ? orgEntity : campaignOrganizerEntity;

  const isFormValid = () => {
    if (isPlatformKey ? !platform : !selectedEntity) return false;
    if (!description || description.length > 20 || !keyType) return false;
    if (isAdvancedInvalid) return false;
    return true;
  };

  const onSave = () => {
    const payload: any = {
      description,
      allowAllSessions: false,
      logActivity: true,
      restrictAdminRoles: false,
      readOnly,
      noSecret,
      advancedPermissions,
      externalIntegrationSystem: isPlatformKey ? platform : null
    };
    if (!isPlatformKey) {
      if (scope === 'org') payload.organizationId = selectedEntity.id;
      else payload.campaignOrganizerId = selectedEntity.id;
    }
    if (advancedPermissions) {
      payload.readAllowed = buildFlagsValue(readAllowed) || null;
      payload.createAllowed = buildFlagsValue(createAllowed) || null;
      payload.editAllowed = buildFlagsValue(editAllowed) || null;
      payload.deleteAllowed = buildFlagsValue(deleteAllowed) || null;
    }
    dispatch(createAccessKey(payload) as any);
  };

  useEffect(() => {
    if (modalLocked) return; // already processed
    const record = accessKeyState.recordCreate;
    if (record?.ready === 0 && record?.data) {
      const data = record.data;
      if (data?.clientKey && data?.clientSecret) {
        setCreatedKey({ clientKey: data.clientKey, clientSecret: data.clientSecret });
        setShowModal(true);
        setModalLocked(true);
      }
    }
  }, [accessKeyState.recordCreate, modalLocked]);

  const getRequest = () => ({
    page: 1,
    pageSize: 50,
    resultType: 'Full',
    orderBy: { key: 'name', direction: 'asc' },
    filters: [],
    includeDataTemplate: true
  });

  const getItem = (item: any) => getEntityOption(item);
  const getOrganizationItem = (item: any) => getOrganizationOption(item);

  const copyToClipboard = async (value: string, type: 'key' | 'secret') => {
    try {
      await navigator.clipboard.writeText(value);
      setCopied((c) => ({ ...c, [type]: true }));
      setTimeout(() => setCopied((c) => ({ ...c, [type]: false })), 2500);
    } catch (e) {
      // fallback: select text logic could be added
    }
  };

  const MonoValue = ({ label, value, type }: { label: string; value: string; type: 'key' | 'secret' }) => (
    <Form.Field>
      <label style={{ fontWeight: 600 }}>{label}</label>
      <Input
        fluid
        readOnly
        value={value}
        action
        style={{ fontFamily: 'monospace' }}
      >
        <input />
        <Button type='button' onClick={() => copyToClipboard(value, type)}>
          {copied[type] ? <Translate value='accessKeys.page.create.secret-modal.copied' /> : <Translate value='accessKeys.page.create.secret-modal.copy' />}
        </Button>
      </Input>
    </Form.Field>
  );

  const ModalContent = () => (
    <div style={{ textAlign: 'left' }}>
      {createdKey && (
        <Form>
          <MonoValue label={I18n.t('accessKeys.page.create.secret-modal.clientKey')} value={createdKey.clientKey} type='key' />
          <MonoValue label={I18n.t('accessKeys.page.create.secret-modal.clientSecret')} value={createdKey.clientSecret} type='secret' />
        </Form>
      )}
      <Message info size='small'>
        <Translate value={'accessKeys.page.create.secret-modal.warning'} />
      </Message>
      <div style={{ textAlign: 'center', marginTop: 20 }}>
        <Button onClick={() => navigate(systemSettingsRoutes.ACCESS_KEYS)} primary>
          <Translate value='accessKeys.page.create.secret-modal.return' />
        </Button>
      </div>
    </div>
  );

  return (
    <div>
  {/* Intentionally omit onDismiss to avoid clearing state that drives modal visibility */}
  <RecordResultMessage record={accessKeyState.recordCreate} />
      <Card fluid style={{ backgroundColor: 'var(--card-subtle-bg)' }}>
        <Card.Content>
          <Form>
            {!isPlatformKey && (
              <>
                <Form.Field>
                  <label>
                    <Translate value={'accessKeys.page.input.scope'} />
                  </label>
                  <Button.Group>
                    <Button
                      type="button"
                      primary={scope === 'org'}
                      onClick={() => setScope('org')}
                    >
                      <Translate value="accessKeys.page.input.org" />
                    </Button>
                    <Button
                      type="button"
                      primary={scope === 'campaignOrganizer'}
                      onClick={() => setScope('campaignOrganizer')}
                    >
                      <Translate value="accessKeys.page.input.campaignOrganizer" />
                    </Button>
                  </Button.Group>
                </Form.Field>
                <Form.Field>
                  {/* Organization selector */}
                  <div style={{ display: scope === 'org' ? 'block' : 'none' }}>
                    <TemplateAutocomplete
                      entity="organization"
                      searchString={orgEntity?.name}
                      label={'accessKeys.page.input.org'}
                      onChange={(_e: any, _v: any, selected: any) =>
                        setOrgEntity(selected)
                      }
                      request={getRequest}
                      searchOnLoad={false}
                      getItem={getOrganizationItem}
                      validation={
                        scope === 'org' ? { required: true } : undefined
                      }
                    />
                  </div>
                  {/* Campaign Organizer selector */}
                  <div
                    style={{
                      display: scope === 'campaignOrganizer' ? 'block' : 'none'
                    }}
                  >
                    <TemplateAutocomplete
                      entity="campaignOrganizer"
                      searchString={campaignOrganizerEntity?.name}
                      label={'accessKeys.page.input.campaignOrganizer'}
                      onChange={(_e: any, _v: any, selected: any) =>
                        setCampaignOrganizerEntity(selected)
                      }
                      request={getRequest}
                      searchOnLoad={false}
                      getItem={getItem}
                      validation={
                        scope === 'campaignOrganizer'
                          ? { required: true }
                          : undefined
                      }
                    />
                  </div>
                </Form.Field>
              </>
            )}
            <Form.Field>
              <label><Translate value={'accessKeys.page.input.keyType'} /></label>
              <Dropdown
                selection
                options={markSystemOnlyOptions(keyTypeOptions)}
                value={keyType}
                onChange={(_e, { value }) => setKeyType(value as any)}
              />
            </Form.Field>
            {isPlatformKey && (
              <Form.Field>
                <label htmlFor="access-key-platform">
                  <Translate value={'accessKeys.page.input.platform'} />
                </label>
                <Dropdown
                  id="access-key-platform"
                  aria-label={I18n.t('accessKeys.page.input.platform')}
                  selection
                  options={registrationPlatformOptions()}
                  value={platform}
                  onChange={(_e, { value }) => setPlatform(value as string)}
                />
              </Form.Field>
            )}
            <Form.Field>
              <ValidatedInput
                value={description}
                label={I18n.t('accessKeys.page.input.description')}
                onChange={(_e: any, { value }: any) => setDescription(value)}
                type='text'
                validation={{ required: true, maxLength: true, length: 20 }}
              />
            </Form.Field>
            {(keyType === KEY_TYPES.OTHER) && (
              <>
                <Form.Field>
                  <Checkbox
                    label={I18n.t('accessKeys.page.input.readOnly')}
                    checked={readOnly}
                    onChange={(_e, { checked }) => setReadOnly(!!checked)}
                  />
                </Form.Field>
                <Form.Field>
                  <Checkbox
                    label={I18n.t('accessKeys.page.input.noSecret')}
                    checked={noSecret}
                    onChange={(_e, { checked }) => setNoSecret(!!checked)}
                  />
                </Form.Field>
                <Form.Field>
                  <Checkbox
                    label={I18n.t('accessKeys.page.input.advancedPermissions')}
                    checked={advancedPermissions}
                    onChange={(_e, { checked }) => setAdvancedPermissions(!!checked)}
                  />
                </Form.Field>
              </>
            )}
            {(keyType === KEY_TYPES.OTHER || isPlatformKey) &&
              advancedPermissions && (
                <div className="advanced-permissions">
                  <p>
                    <Translate
                      value={'accessKeys.page.input.advancedPermissionsNote'}
                    />
                  </p>
                  <Form.Field>
                    <label>
                      <Translate value="accessKeys.page.input.readAllowed" />
                    </label>
                    <Dropdown
                      multiple
                      selection
                      options={operationEntityFlags}
                      value={readAllowed}
                      onChange={(_e, { value }) =>
                        setReadAllowed(value as number[])
                      }
                    />
                  </Form.Field>
                  <Form.Field>
                    <label>
                      <Translate value="accessKeys.page.input.createAllowed" />
                    </label>
                    <Dropdown
                      multiple
                      selection
                      options={operationEntityFlags}
                      value={createAllowed}
                      onChange={(_e, { value }) =>
                        setCreateAllowed(value as number[])
                      }
                    />
                  </Form.Field>
                  <Form.Field>
                    <label>
                      <Translate value="accessKeys.page.input.editAllowed" />
                    </label>
                    <Dropdown
                      multiple
                      selection
                      options={operationEntityFlags}
                      value={editAllowed}
                      onChange={(_e, { value }) =>
                        setEditAllowed(value as number[])
                      }
                    />
                  </Form.Field>
                  <Form.Field>
                    <label>
                      <Translate value="accessKeys.page.input.deleteAllowed" />
                    </label>
                    <Dropdown
                      multiple
                      selection
                      options={operationEntityFlags}
                      value={deleteAllowed}
                      onChange={(_e, { value }) =>
                        setDeleteAllowed(value as number[])
                      }
                    />
                  </Form.Field>
                  {isAdvancedInvalid && (
                    <Text style={{ color: 'red' }}>
                      <Translate
                        value={
                          'accessKeys.page.input.advancedPermissionsValidation'
                        }
                      />
                    </Text>
                  )}
                </div>
              )}
          </Form>
        </Card.Content>
      </Card>
      <Button primary disabled={!isFormValid()} onClick={onSave}><Translate value={'accessKeys.page.input.save'} /></Button>
      <Button onClick={()=>navigate(systemSettingsRoutes.ACCESS_KEYS)}><Translate value={'accessKeys.page.input.cancel'} /></Button>
      <SavedEntityModal
        open={showModal}
        header={I18n.t('accessKeys.page.create.secret-modal.title')}
        content={<ModalContent />}
        buttons={<></>}
        onClose={()=>{ setShowModal(false); navigate(systemSettingsRoutes.ACCESS_KEYS); }}
      />
    </div>
  );
};

export default AccessKeyInput;
